import { useState } from 'react'
import clsx from 'clsx'
import { useAtomValue } from 'jotai'
import { Trans, useTranslation } from 'react-i18next'
import { ButtonNextLink } from '@/components/ButtonNextLink'
import { CampaignCard } from '@/components/InfoCard/InfoCard'
import { Pillow } from '@/components/Pillow/Pillow'
import { BUNDLE_DISCOUNT_PERCENTAGE } from '@/features/bundleDiscount/bundleDiscount.constants'
import { getBundleDiscountOnCurrentOffer } from '@/features/bundleDiscount/bundleDiscount.utils'
import { ExternalInsuranceCancellationNotice } from '@/features/CrossSell/components/RecommendedOffer/ExternalInsuranceCancellationNotice'
import { RecommendedOffer } from '@/features/CrossSell/components/RecommendedOffer/RecommendedOffer'
import { RecommendedProduct } from '@/features/CrossSell/components/RecommendedOffer/RecommendedProduct'
import { type OfferRecommendation, useRecommendations } from '@/features/CrossSell/hooks/useRecommendations'
import { useShopSessionValueOrThrow } from '@/services/shopSession/ShopSessionContext'
import { useTracking } from '@/services/Tracking/useTracking'
import type { Money } from '@/utils/formatter'
import { useRoutingLocale } from '@/utils/l10n/useRoutingLocale'
import { PageLink } from '@/utils/PageLink'
import { useBreakpoint } from '@/utils/useBreakpoint/useBreakpoint'
import { useFormatter } from '@/utils/useFormatter'
import { IconButton } from 'ui/src/components/Button/IconButton'
import { Card } from 'ui/src/components/Card/Card'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { ToggleTip } from 'ui/src/components/Tooltip/ToggleTip'
import { CloverIcon } from 'ui/src/icons/CloverIcon'
import { InfoIcon } from 'ui/src/icons/InfoIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { tokens } from 'ui/src/theme/tokens.css'
import { priceCalculatorAddedOffer } from '../priceCalculatorAtoms'
import { ribbon } from './PurchaseSymmary.css'

export function PurchaseSummaryWithRecommendations({ className }: { className?: string }) {
  const shopSession = useShopSessionValueOrThrow()
  const isDesktop = useBreakpoint('lg')
  const { t } = useTranslation(['purchase-form', 'cart', 'common'])
  const locale = useRoutingLocale()
  const addedOffer = useAtomValue(priceCalculatorAddedOffer)
  const tracking = useTracking()
  const { offerRecommendations, productRecommendations } = useRecommendations()
  const [addedRecommendations, setAddedRecommendations] = useState<Array<OfferRecommendation>>([])
  const [isRecommendedOfferAddedDialogOpen, setIsRecommendedOfferAddedDialogOpen] = useState(false)
  const [lastAddedInsurerName, setLastAddedInsurerName] = useState('')

  if (!addedOffer) {
    return null
  }

  const handleGoToCart = () => {
    tracking.reportBeginCheckout(shopSession.cart)
  }

  const handleAddRecommendedOffer = (rec: OfferRecommendation) => {
    const priceMatch = rec.offer.priceMatch
    if (priceMatch) {
      setLastAddedInsurerName(priceMatch.externalInsurer.displayName)
    }
    setAddedRecommendations((prev) => [...prev, rec])
    setIsRecommendedOfferAddedDialogOpen(true)
  }

  const addedRecommendationIds = new Set(addedRecommendations.map((rec) => rec.offer.id))

  const externalOfferRecommendations = offerRecommendations.filter(
    (rec) => rec.externalInsurance != null && !addedRecommendationIds.has(rec.offer.id),
  )

  const hasExternalRecommendations = externalOfferRecommendations.length > 0 || productRecommendations.length > 0

  const liveAddedOffer =
    shopSession.cart.items.find((item) => item.productOffer.id === addedOffer.id)?.productOffer ?? addedOffer

  const bundleDiscountOnCurrentOffer = getBundleDiscountOnCurrentOffer(liveAddedOffer)

  // Show the Bundle Discount Potential Savings for an eligible product for a purchase that
  // doesn't qualify for bundle discount yet
  const shouldShowBundleDiscountInfoCard =
    !bundleDiscountOnCurrentOffer && addedOffer.bundleDiscount.potentialYearlySavings != null
  const shouldShowBundleDiscountRibbon = bundleDiscountOnCurrentOffer != null

  return (
    <div className={clsx(yStack({ gap: { _: 'xxl', md: 'xxxl' } }), className)}>
      <Card.Root>
        {shouldShowBundleDiscountRibbon && (
          <Card.Ribbon className={ribbon}>
            <CloverIcon size="1rem" color={tokens.colors.signal.green.element} />
            <Text as="p" size="xs" color="signalGreenText" align="center" singleLine>
              {t('cart:BUNDLE_DISCOUNT_RIBBON', { percentage: BUNDLE_DISCOUNT_PERCENTAGE })}
            </Text>
          </Card.Ribbon>
        )}

        <div className={yStack({ gap: 'lg' })}>
          <Card.Header>
            <Card.Media>
              <Pillow size="small" {...addedOffer.product.pillowImage} />
            </Card.Media>
            <Card.Heading>
              <Card.Title>
                {isDesktop ? addedOffer.product.displayNameFull : addedOffer.product.displayNameShort}
              </Card.Title>
              <Card.Subtitle>{addedOffer.exposure.displayNameShort}</Card.Subtitle>
            </Card.Heading>
          </Card.Header>

          {addedRecommendations.map((rec) => (
            <AddedRecommendationSection key={rec.offer.id} recommendation={rec} isDesktop={isDesktop} />
          ))}
        </div>

        {shouldShowBundleDiscountInfoCard && <InfoCard savings={addedOffer.bundleDiscount.potentialYearlySavings!} />}

        <ButtonNextLink
          href={PageLink.checkout({ locale }).toRelative()}
          onClick={handleGoToCart}
          variant="primary"
          size="large"
        >
          {t('cart:PURCHASE_SUMMARY_GO_TO_CART_LABEL')}
        </ButtonNextLink>
      </Card.Root>

      {hasExternalRecommendations && (
        <section className={yStack({ gap: 'lg' })}>
          <header>
            <div className={xStack({ alignItems: 'center', gap: 'xs', justifyContent: 'space-between' })}>
              <Heading as="h2" variant="standard.24">
                {t('FETCHED_INSURANCES_SECTION_TITLE', { ns: 'cart' })}
              </Heading>

              <ToggleTip
                Trigger={
                  <IconButton
                    data-no-hover
                    Icon={<InfoIcon size="1.5rem" color={tokens.colors.fills.opaque.secondary} />}
                  />
                }
              >
                <Text size="xs" color="textNegative" align="center">
                  {t('FETCHED_INSURANCES_SECTION_TOOLTIP', { ns: 'cart' })}
                </Text>
              </ToggleTip>
            </div>

            <Heading as="h2" variant="standard.24" color="textSecondary">
              {t('PURCHASE_SUMMARY_FETCHED_INSURANCES_SECTION_SUBTITLE', { ns: 'cart' })}
            </Heading>
          </header>

          <div className={yStack({ gap: 'md' })}>
            {externalOfferRecommendations.map((rec) => (
              <RecommendedOffer
                key={rec.offer.id}
                recommendation={rec}
                shopSessionId={shopSession.id}
                onAddToCart={() => handleAddRecommendedOffer(rec)}
              />
            ))}

            {productRecommendations.map((rec) => (
              <RecommendedProduct key={rec.product.id} recommendation={rec} />
            ))}
          </div>
        </section>
      )}

      <ButtonNextLink href={PageLink.store({ locale })} variant="secondary" size="large">
        {t('common:ALL_OUR_INSURANCES')}
      </ButtonNextLink>

      <ExternalInsuranceCancellationNotice.Root
        open={isRecommendedOfferAddedDialogOpen}
        onOpenChange={setIsRecommendedOfferAddedDialogOpen}
        insurerName={lastAddedInsurerName}
      >
        <ExternalInsuranceCancellationNotice.ConfirmButton
          as={ButtonNextLink}
          href={PageLink.checkout({ locale }).toRelative()}
          onClick={handleGoToCart}
          size="large"
          fullWidth={true}
        />
      </ExternalInsuranceCancellationNotice.Root>
    </div>
  )
}

type AddedRecommendationSectionProps = {
  recommendation: OfferRecommendation
  isDesktop: boolean
}

function AddedRecommendationSection({ recommendation }: AddedRecommendationSectionProps) {
  const { offer, product } = recommendation

  const productTitle = [offer.product.displayNameFull, offer.variant.displayNameTier].filter(Boolean).join(' ')
  const externalInsuranceName = offer.priceMatch?.externalInsuranceName

  return (
    <Card.Header>
      <Card.Media>
        <Pillow size="small" src={product.pillowImage.src} alt={product.pillowImage.alt ?? ''} />
      </Card.Media>
      <Card.Heading>
        <Card.Title>{productTitle}</Card.Title>
        <Card.Subtitle>{externalInsuranceName ?? offer.exposure?.displayNameShort}</Card.Subtitle>
      </Card.Heading>
    </Card.Header>
  )
}

function InfoCard({ savings }: { savings: Money }) {
  const formatter = useFormatter()

  const potentialYearlySavings = formatter.money(savings)

  return (
    <CampaignCard>
      <Text color="signalGreenText" size="sm">
        <Trans
          i18nKey="BUNDLE_DISCOUNT_POTENTIAL_SAVING"
          ns="cart"
          components={{ strong: <strong className={sprinkles({ color: 'textPrimary' })} /> }}
          values={{
            percentage: BUNDLE_DISCOUNT_PERCENTAGE,
            potentialSaving: potentialYearlySavings,
          }}
        />
      </Text>
    </CampaignCard>
  )
}
